import React, { Children, forwardRef, useCallback, useState, useRef, useEffect, useImperativeHandle } from 'react'

import TreeList, {
    TreeListRef,
    ColumnChooser,
    FilterRow,
    HeaderFilter,
    LoadPanel,
    Pager,
    Paging,
    SearchPanel,
    Selection,
    Scrolling,
    RowDragging,
    StateStoring,
} from 'devextreme-react/tree-list'

import { Template } from 'devextreme-react/core/template'
import { EditorPreparingEvent } from 'devextreme/ui/data_grid'
import { ISelectionProps } from 'devextreme-react/data-grid'
import { IxBadge } from 'components/badge'

import { LayoutChooser } from 'components/layout-chooser'
import { useIxGlobalFilter } from 'contexts/global-filter-context'
import { filterBuilder } from 'helpers/filter-builder'
import { useTranslation } from 'react-i18next'
import { Selection as SelectionEnum } from 'enums/selection'
import _ from 'lodash'

import { useIxOnScreen } from 'hooks/use-on-screen'

import EndLine from './end-line.svg?react'
import VLine from './vertical-line.svg?react'
import BLine from './bottom-half-line.svg?react'
import TLine from './top-half-line.svg?react'

import './base-tree-list.scss'

export type IBaseTreeListProps = {
    name?: string,
    loading?: boolean,
    items: any[],
    onSelectionChanged?: (ids: any[]) => void,
    children?: React.ReactNode[],
    objectFilterField: string,
    fromFilterField: string,
    selectionMode?: ISelectionProps['mode'],
    keyExpr?: string,
    parentIdExpr: string,
    firstColumn?: string,
    iconsFn?: (row: any) => string,
    selectedRowKeys?: any[],
    onDblClick?: (e: any[]) => void,
    customToolbarItems?: any[]
    customizeColumns?: any,
    enabledLoadPanel?: boolean | 'auto',
    height?: any,
    onEditorPreparing?: (e: EditorPreparingEvent) => void
    enablePaging?: boolean,
    draggingProps?: any,
    defaultSelectionFilter?: any[],
    deferredSelection?: boolean,
    columnRenderingMode?: 'standard' | 'virtual',
    rowContextMenu?: Function,
    showCheckBoxesMode?: 'none' | 'onClick' | 'always' | 'onLongTap' | undefined,
    selectedItems?: any[],
    selection?: SelectionEnum
}

let singleClick = false

export const BaseTreeList = forwardRef<(TreeListRef), IBaseTreeListProps>((
    {
        name, items, onSelectionChanged, onDblClick, objectFilterField,
        fromFilterField, selectionMode='multiple', customToolbarItems,
        enabledLoadPanel='auto', onEditorPreparing, deferredSelection=false,
        columnRenderingMode='virtual', draggingProps, enablePaging=true,
        rowContextMenu, showCheckBoxesMode='onClick', firstColumn, iconsFn,
        children, selectedItems, selection, ...props
    },
    ref
) => {
    const treelist = useRef<TreeListRef>(null)
    const divRef = useRef<HTMLDivElement>(null)
    const onScreen = useIxOnScreen(divRef)
    const { objectFilter, fromFilter } = useIxGlobalFilter()
    const { i18n } = useTranslation()
    const [layoutsChooserVisible, setLayoutsChooserVisible] = useState(false)
    const [rowHeight, setRowHeight] = useState(100)

    const _firstRender = useRef(true)

    useImperativeHandle(ref, () => treelist.current! )

    const toggleLayoutChooser = useCallback(() => {
        setLayoutsChooserVisible(visible => !visible)
    }, [])

    const onToolbarPreparing = useCallback((e: any) => {
        let toolbarItems = e.toolbarOptions.items;
         toolbarItems.splice(2, 0, {
            widget: 'dxButton',
            options: {
                icon: 'detailslayout',
                onClick: toggleLayoutChooser
            },
            location: 'after'
        })

        if (customToolbarItems) {
            toolbarItems.unshift(...customToolbarItems)
        }
    }, [toggleLayoutChooser, customToolbarItems])

    const _onSelectionChanged = useCallback((e: any) => {
        if (props.selectedRowKeys !== undefined && !deferredSelection) {
            let selectedRows = props.selectedRowKeys.filter((r: any) => e.currentDeselectedRowKeys.indexOf(r) === -1)
            if (!_.isEqual(selectedRows, e.currentSelectedRowKeys)) {
                selectedRows = selectedRows.concat(e.currentSelectedRowKeys)
            }
            onSelectionChanged && onSelectionChanged(selectedRows)
        } else {
            onSelectionChanged && onSelectionChanged(e.selectedRowsData)
        }
    }, [onSelectionChanged, props, deferredSelection])

    const onContextMenuPreparing = useCallback((e: any) => {
        if (e.target === 'content' && e.row?.rowType === 'data') {
            rowContextMenu && rowContextMenu(e)
        }
    }, [rowContextMenu])

    useEffect(() => {
        if (props.loading) {
            /* v8 ignore next */
            treelist.current?.instance && treelist.current.instance().beginCustomLoading('loading')
        } else {
            /* v8 ignore next */
            treelist.current?.instance && treelist.current.instance().endCustomLoading()
        }
    }, [props.loading])

    useEffect(() => {
        if (treelist.current) {
            if ((objectFilter && objectFilterField) || (fromFilter && fromFilterField)) {
                const filter = filterBuilder(objectFilter, objectFilterField, fromFilter, fromFilterField)

                /* v8 ignore next 5 */
                if (filter.length) {  // don't apply filter if it's an empty array
                    treelist.current?.instance && treelist.current.instance().filter(filter)
                } else {
                    treelist.current?.instance && treelist.current.instance().clearFilter('dataSource')
                }
            } else {
                /* v8 ignore next */
                treelist.current?.instance && treelist.current.instance().clearFilter('dataSource')
            }
        }
    }, [objectFilter, objectFilterField, fromFilter, fromFilterField])

    useEffect(() => {
        /* v8 ignore next 3 */
        const repaint = () => {
            treelist.current?.instance().repaint()
        }

        i18n.on('languageChanged', repaint)

        return () => { i18n.off('languageChanged', repaint) }
    }, [i18n])

    const _onEditorPreparing = useCallback((e: EditorPreparingEvent) => {
        if (e.editorName !== 'dxSelectBox') {
            e.editorOptions.valueChangeEvent = 'change'
        }
        onEditorPreparing && onEditorPreparing(e)
    }, [onEditorPreparing])

    const selectedContextBadge = useCallback((data: any) => {
        return (
            <div style={ { display: 'flex', alignItems: 'center' } }><IxBadge value={ data.nSelected } /> SELECTED</div>
        )
    }, [])

    const saveState = useCallback((state: any) => {
        const { selectionFilter, selectedRowKeys, ...partialState } = state
        localStorage.setItem(`${name}-treelist`, JSON.stringify(partialState))
    }, [name])

    const loadState = useCallback(() => {
        const state = localStorage.getItem(`${name}-treelist`)
        const parsedState = state ? JSON.parse(state) : undefined

        if (parsedState && parsedState.columns && _.sortBy(parsedState.columns, 'visibleIndex')[0].dataField !== firstColumn) {
            parsedState.columns = parsedState.columns.map((col: any) => {
                if (col.dataField === firstColumn) { col.visibleIndex = 0 }
                else { col.visibleIndex += 1 }
                return col
            })
        }

        return parsedState
    }, [name, firstColumn])

    const onCellPrepared = useCallback((e: any) => {
        if (e.column.dataField === 'AUFGABESTATUS' || e.column.dataField === 'AKTIONSTATUS') {
            e.value === 2 && (e.cellElement.style.color = 'red')
            e.value === 1 && (e.cellElement.style.color = 'green')
            e.value === 0 && (e.cellElement.style.color = 'blue')
        }
    },[])

    const fCellRender = useCallback((r: any) => {
        let icon = iconsFn ? (<span className={`ix-treelist-icon dx-icon dx-icon-${iconsFn(r.data)}`} />) : null
        if (r.row.level > 0) {
            const prevLines = [...Array(r.row.level)].map((__, i) => {
                let parent = r.row.node.parent
                for (let j = 0; j < r.row.level - i - 1; j++) {
                    parent = parent.parent
                }

                if (parent.level === 0 || parent.parent.children.length - 1 !== _.findIndex(parent.parent.children, parent)) {
                    // return (<img src={ vLine } alt='' style={ { marginRight: '30px' } } />)
                    return (<VLine key={i} height={rowHeight} style={ { marginRight: '30px' } } />)
                } else {
                    return (<span key={i} style={ { marginRight: '30px' } } />)
                }
            })
            return (
                <div className='ix-folder-lines'>
                    <div className='ix-folder-lines-content'>
                        { prevLines }
                        { r.row.node.parent.children.length - 1 === _.findIndex(r.row.node.parent.children, r.row.node) ? <TLine height={rowHeight} /> : (<VLine height={rowHeight} />) }
                        {/* <img src={ (r.row.level !== nextRowLevel) ? tLine : vLine } alt='' /> */}
                        <EndLine />
                    </div>
                    { icon }
                    <div>{ r.data[r.column.dataField] }</div>
                </div>
            )
        } else {
            return (
                <div className='ix-folder-lines'>
                    <div className='ix-folder-lines-content'>
                    { r.rowIndex === 0 ? (<BLine height={rowHeight} />) : (<VLine height={rowHeight} />) }
                        {/* <img src={ r.rowIndex === 0 ? bLine : vLine } alt='' /> */}
                        {/* <img src={ endLine } alt='' /> */}
                        <EndLine />
                    </div>
                    { icon }
                    <div>{ r.data[r.column.dataField] }</div>
                </div>
            )
        }
    }, [rowHeight, iconsFn])

    const onContentReady = useCallback((e: any) => {
        if (_firstRender.current && e.component.getRowElement(0)) {
            _firstRender.current = false
            let rowElement = e.component.getRowElement(0)
            if (Array.isArray(rowElement) && rowElement.length > 0) {
                rowElement = rowElement[0]
            }
            setRowHeight(rowElement.clientHeight - 1 || 26)
        }
    }, [])

    const _onRowClick = useCallback((e: any) => {
        if (e.event.detail === 1) {
            singleClick = true
            setTimeout(() => {
                if (!singleClick) { return }
                if (!e.isSelected) {
                    e.component.selectRows([e.key], selectionMode === 'multiple' && (selection && selection > SelectionEnum.SingleSelection))
                } else {
                    e.component.deselectRows([e.key])
                }
            }, 500)
        }
    }, [selectionMode, selection])

    const _onDblClick = useCallback((e: any) => {
        singleClick = false
        const selectedRows = e.component.getSelectedRowKeys()
        if (selectedRows.length > 1) { return }
        if (!e.isSelected) {
            e.component.selectRows([e.key], false).then((rows: any) => {
                setTimeout(() => { onDblClick && onDblClick(rows) })
            })
        } else {
            onDblClick && onDblClick(selectedRows)
        }
    }, [onDblClick])

    useEffect(() => {
        if (!onScreen || !selectedItems) { return }
        setTimeout(() => treelist.current?.instance().selectRows(selectedItems.map((x: any) => x.HDR_ID), false), 0)
    }, [onScreen, selectedItems])

    return (
        <div className={`ix-base-treelist ix-base-treelist-sel-${selectionMode}`} ref={ divRef }>
            <LayoutChooser
                gridName={name || ''}
                datagrid={treelist}

                visible={layoutsChooserVisible}
                onHiding={ () => setLayoutsChooserVisible(false) }
            />

            <TreeList
                id={`${name}-treelist`}
                ref={ treelist }

                dataSource={items}
                keyExpr={props.keyExpr || 'HDR_ID'}
                parentIdExpr={props.parentIdExpr}
                allowColumnReordering={true}
                allowColumnResizing={true}
                showBorders={true}
                onToolbarPreparing={onToolbarPreparing}
                onSelectionChanged={_onSelectionChanged}
                onContextMenuPreparing={onContextMenuPreparing}
                columnAutoWidth={true}
                columnMinWidth={100}
                columnResizingMode='widget'
                onRowDblClick={_onDblClick}
                onCellPrepared={onCellPrepared}
                onContentReady={ onContentReady }
                // repaintChangesOnly={ true }
                { ...props.height && {height: props.height} }
                // if `props.selectedRowKeys` is not provided we don't set the
                // datagrid `selectedRowKeys` prop so selection still works
                { ...(props.selectedRowKeys ? { selectedRowKeys: props.selectedRowKeys } : {}) }
                { ...(props.customizeColumns ? {customizeColumns: props.customizeColumns} : {}) }
                onEditorPreparing={_onEditorPreparing}
                // { ...(props.defaultSelectionFilter ? { defaultSelectionFilter: props.defaultSelectionFilter } : {}) }
                rowAlternationEnabled={ false }
                onRowClick={_onRowClick}
            >
                { draggingProps && <RowDragging {...draggingProps} /> }
                <SearchPanel visible={true} />
                <Paging defaultPageSize={50} enabled={enablePaging}/>
                <Pager showPageSizeSelector={true} allowedPageSizes={[10, 25, 50, 100]} />
                <FilterRow visible={true} />
                <HeaderFilter visible={true} />
                <LoadPanel
                    enabled={enabledLoadPanel}
                    showIndicator={true}
                />
                <ColumnChooser enabled={true} />
                <Selection
                    mode={ selectionMode }
                />
                <Scrolling
                    scrollByContent={true}
                    columnRenderingMode={ columnRenderingMode }
                    mode='standard'
                    // rowRenderingMode='virtual'
                />
                <Template name='selectedContextBadge' render={ selectedContextBadge } />
                <StateStoring
                    enabled={ true }
                    savingTimeout={ 1000 }
                    type='custom'
                    customLoad={ loadState }
                    customSave={ saveState }
                />
                { Children.map(children, (child) => ( React.isValidElement(child) && child.props.dataField === firstColumn ? React.cloneElement<any>(child, { cellRender: fCellRender, visibleIndex: 0, allowReordering: false, allowHiding: false }) : child )) }
            </TreeList>
        </div>
    )
})
